import { ThemeEvents } from '@theme/events';

/**
 * @param {string} href
 */
const normalizeCollectionHref = (href) => {
  try {
    return new URL(href, window.location.origin).toString();
  } catch {
    const [path, ...queryParts] = href.split('?');
    if (queryParts.length <= 1) return href;

    const search = queryParts.join('&');
    return `${path}?${search}`;
  }
};

/**
 * @param {string} paramName
 * @param {string} filterValue
 * @param {string} [filterLabel]
 * @param {URLSearchParams} queryParams
 */
const isQuickSelectionFilterActive = (paramName, filterValue, filterLabel, queryParams) => {
  if (paramName && filterValue && queryParams.getAll(paramName).includes(filterValue)) {
    return true;
  }

  for (const [key, value] of queryParams.entries()) {
    if (!key.startsWith('filter.')) continue;
    if (value === filterValue) return true;
    if (filterLabel && value === filterLabel) return true;
  }

  return false;
};

/**
 * @param {URLSearchParams} [queryParams]
 */
const syncQuickSelectionFilters = (queryParams = new URLSearchParams(window.location.search)) => {
  for (const root of document.querySelectorAll('.nf-quick-selection-filters')) {
    const sectionId = root instanceof HTMLElement ? root.dataset.sectionId : '';

    for (const chip of root.querySelectorAll('[data-nf-quick-selection-filter]')) {
      if (!(chip instanceof HTMLAnchorElement)) continue;

      const paramName = chip.dataset.paramName ?? '';
      const filterValue = chip.dataset.filterValue ?? '';
      const filterLabel = chip.dataset.filterLabel ?? '';

      let isActive = isQuickSelectionFilterActive(paramName, filterValue, filterLabel, queryParams);

      if (!isActive && sectionId) {
        const drawerForm = document.getElementById(`FacetFiltersForm--${sectionId}-overflow`);
        const desktopForm = document.getElementById(`FacetFiltersForm--${sectionId}-desktop`);

        for (const form of [drawerForm, desktopForm]) {
          if (!(form instanceof HTMLFormElement)) continue;

          const input = form.querySelector(
            `input[name="${CSS.escape(paramName)}"][value="${CSS.escape(filterValue)}"]`
          );

          if (input instanceof HTMLInputElement && input.checked) {
            isActive = true;
            break;
          }
        }
      }

      chip.classList.toggle('nf-quick-selection-filter__chip--active', isActive);
      chip.setAttribute('aria-pressed', String(isActive));
    }
  }
};

/** @param {MouseEvent} event */
const onQuickSelectionClick = (event) => {
  const chip = event.target instanceof Element ? event.target.closest('[data-nf-quick-selection-filter]') : null;
  if (!(chip instanceof HTMLAnchorElement) || chip.classList.contains('nf-quick-selection-filter__chip--disabled')) return;

  event.preventDefault();

  const paramName = chip.dataset.paramName;
  const filterValue = chip.dataset.filterValue;
  const sectionId = chip.dataset.sectionId ?? chip.closest('.nf-quick-selection-filters')?.dataset.sectionId ?? '';

  if (!paramName || !filterValue) {
    window.location.assign(normalizeCollectionHref(chip.href));
    return;
  }

  const drawerForm = sectionId ? document.getElementById(`FacetFiltersForm--${sectionId}-overflow`) : null;
  if (!drawerForm) {
    window.location.assign(normalizeCollectionHref(chip.href));
    return;
  }

  const input = drawerForm.querySelector(
    `input[name="${CSS.escape(paramName)}"][value="${CSS.escape(filterValue)}"]`
  );

  if (!(input instanceof HTMLInputElement)) {
    window.location.assign(normalizeCollectionHref(chip.href));
    return;
  }

  const willBeActive = !input.checked;
  chip.classList.toggle('nf-quick-selection-filter__chip--active', willBeActive);
  chip.setAttribute('aria-pressed', String(willBeActive));

  input.checked = willBeActive;
  const facetInputs = input.closest('facet-inputs-component');
  if (facetInputs && 'updateFilters' in facetInputs && typeof facetInputs.updateFilters === 'function') {
    facetInputs.updateFilters();
    return;
  }

  input.dispatchEvent(new Event('change', { bubbles: true }));
};

/** @param {Event} event */
const onFilterUpdate = (event) => {
  const queryParams =
    event instanceof CustomEvent && event.detail?.queryParams instanceof URLSearchParams
      ? event.detail.queryParams
      : new URLSearchParams(window.location.search);

  syncQuickSelectionFilters(queryParams);
};

document.addEventListener('click', onQuickSelectionClick);
document.addEventListener(ThemeEvents.FilterUpdate, onFilterUpdate);
syncQuickSelectionFilters();

document.addEventListener('shopify:section:load', () => {
  syncQuickSelectionFilters();
});
